import { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';

interface AuthUser {
  email: string;
  roles: string[];
}

interface AuthState {
  user: AuthUser | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  isAdmin: boolean;
}

export function useAuth(): AuthState & { logout: () => Promise<void>; refetch: () => Promise<void> } {
  const [state, setState] = useState<AuthState>({
    user: null,
    isLoading: true,
    isAuthenticated: false,
    isAdmin: false,
  });
  const navigate = useNavigate();

  const fetchUser = useCallback(async () => {
    try {
      const res = await fetch('/auth/me', { credentials: 'include' });
      if (!res.ok) {
        setState({ user: null, isLoading: false, isAuthenticated: false, isAdmin: false });
        return;
      }
      const data = await res.json();
      const roles: string[] = data.roles || [];
      setState({
        user: { email: data.email, roles },
        isLoading: false,
        isAuthenticated: true,
        isAdmin: roles.includes('admin'),
      });
    } catch {
      setState({ user: null, isLoading: false, isAuthenticated: false, isAdmin: false });
    }
  }, []);

  useEffect(() => {
    fetchUser();
  }, [fetchUser]);

  const logout = useCallback(async () => {
    try {
      await fetch('/auth/logout', { method: 'POST', credentials: 'include' });
    } finally {
      setState({ user: null, isLoading: false, isAuthenticated: false, isAdmin: false });
      navigate('/login');
    }
  }, [navigate]);

  return { ...state, logout, refetch: fetchUser };
}
